// SiteSwitcher — the grid button beside the theme toggle in the tab bar: a
// directory of the other Stride deployments, for the people who work
// across them.
//
// Each site is another ORIGIN with its own session, so a row is a plain
// link: the browser's session for that origin signs the person straight in
// (sliding 7-day token), or that site's login shows once. Nothing here
// crosses sites — no token, no identity — which is exactly why it fits the
// fork-per-client design without touching auth.
//
// Visibility is ONE gate, the group: the SERVER answers GET /api/sites
// with a non-empty list only to members of the support group
// (app-shell/server/sites.js) — the frontend merely avoids asking when it
// already knows it is not one. A support member sees the button whenever
// they are signed in; admin mode is not involved (it was, briefly, on the
// first live walk — it added nothing the group does not already decide).
// No list, no button: a deployment without STRIDE_SITES has no switcher.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { SITE_SWITCH_GROUP } from '@client/config/client.config';
import '@platform/app-shell/frontend/components/SiteSwitcher/SiteSwitcher.css';

// A site is "this one" when its origin is the page's origin — the list is
// the same on every deployment that carries it, so each marks itself.
function isCurrentSite(url) {
  try { return new URL(url).origin === window.location.origin; } catch { return false; }
}

// A site's own icon, fetched from ITS origin: the favicon an admin saved
// in that site's Look and Feel (served as the app icon; 404 when none),
// else the product mark every deployment ships. Plain <img> loads need
// no CORS. When neither loads, the initials badge stands in — the row
// never shows a broken image.
function siteIconCandidates(url) {
  try {
    const origin = new URL(url).origin;
    return [`${origin}/api/branding/app-icon.png`, `${origin}/pwa-192x192.png`];
  } catch { return []; }
}

function SiteIcon({ site }) {
  const [index, setIndex] = useState(0);
  const candidates = siteIconCandidates(site.url);
  if (index >= candidates.length) {
    return (
      <span className={`site-switcher-badge site-tone-${site.tone ?? 0}`} aria-hidden="true">
        {site.initials || '?'}
      </span>
    );
  }
  return (
    <img
      className="site-switcher-icon"
      src={candidates[index]}
      alt=""
      aria-hidden="true"
      referrerPolicy="no-referrer"
      onError={() => setIndex((i) => i + 1)}
    />
  );
}

const GRID_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {[4, 12, 20].flatMap((y) => [4, 12, 20].map((x) => (
      <circle key={`${x}-${y}`} cx={x} cy={y} r="2.1" />
    )))}
  </svg>
);

export default function SiteSwitcher() {
  const { userGroups, impersonation } = useAuth();
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  // The panel is a PORTAL on document.body, position:fixed at viewport
  // coordinates from the trigger. Two reasons it cannot live inside the
  // tab bar: the bar clips its overflow (fixed height), and the bar is a
  // stacking context at z-index 300 — page chrome below it (sticky
  // headers, toolbars) reaches 1000+, so a panel painted inside the bar's
  // layer opened BEHIND the page and the click looked dead (second live
  // walk, 2026-09-09). At the root the panel's own z-index wins.
  const [panelPos, setPanelPos] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  // Anchored under the trigger's RIGHT edge. The panel inherits tn-dropdown,
  // which sets left:0 for the TopNav's menus; with left AND right set the
  // browser keeps left, so left:auto is pinned INLINE where no stylesheet
  // order can undo it (the second live walk found the panel parked at the
  // viewport's left edge, a screen away from the button).
  const toggle = () => {
    if (open) { setOpen(false); return; }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPanelPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) });
    setOpen(true);
  };
  const isSwitcher = Array.isArray(userGroups) && userGroups.includes(SITE_SWITCH_GROUP);

  useEffect(() => {
    if (!isSwitcher || impersonation) { setSites([]); return; }
    let alive = true;
    api.getSites()
      .then((r) => { if (alive) setSites(Array.isArray(r?.sites) ? r.sites : []); })
      .catch(() => { if (alive) setSites([]); });
    return () => { alive = false; };
  }, [isSwitcher, impersonation]);

  useEffect(() => {
    if (!open) return undefined;
    // The panel is not a DOM descendant of the trigger (portal), so an
    // "outside" press is one outside BOTH.
    const onDown = (e) => {
      const inTrigger = rootRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onResize = () => setOpen(false); // coordinates go stale; reopen re-measures
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  if (!isSwitcher || sites.length === 0) return null;

  return (
    <div className="site-switcher" ref={rootRef}>
      <button
        type="button"
        className={`tn-trigger site-switcher-trigger ${open ? 'tn-trigger--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Switch site"
        title="Switch site"
        ref={triggerRef}
        onClick={toggle}
      >
        {GRID_ICON}
      </button>
      {open && panelPos && createPortal(
        <div ref={panelRef} className="tn-dropdown site-switcher-panel" role="menu" aria-label="Sites" style={{ top: panelPos.top, right: panelPos.right, left: 'auto' }}>
          <div className="site-switcher-heading">Switch site</div>
          {sites.map((site) => {
            const current = isCurrentSite(site.url);
            const badge = <SiteIcon site={site} />;
            if (current) {
              return (
                <div key={site.url} className="site-switcher-row site-switcher-row--current" role="menuitem" aria-current="true">
                  {badge}
                  <span className="site-switcher-name">{site.name}</span>
                  <span className="site-switcher-current">Current</span>
                </div>
              );
            }
            return (
              <a
                key={site.url}
                className="site-switcher-row"
                role="menuitem"
                href={site.url}
                rel="noopener"
                onClick={() => setOpen(false)}
              >
                {badge}
                <span className="site-switcher-name">{site.name}</span>
              </a>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
